import {
  useActiveCamera,
  type CameraSwitchEvent,
} from "@/components/r3f/cameras/active-camera-context";
import { PHOTO_SIZE } from "@/object-cache";
import { useAppStore } from "@/store/store-hooks";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { CameraViewType } from "@/utils/cam-view";
import {
  getSheetSize,
  selectIElementWorldTransform,
  useNonExhaustiveEffect,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { isIElementImg2d } from "@faro-lotv/ielement-types";
import { useCallback, useLayoutEffect, useRef } from "react";
import type { FlyWalkAnimation } from "./data-dispatch/flywalk-animation";
import type { FlyWalkSceneActiveElement } from "./flywalk-types";

/**
 * This hook enables the caller component to use an ortho camera when the flywalk mode is rendering a 2D image or an orthomosaic,
 * It should be kept in sync with the complementary hook 'usePerspectiveCameraForNonImages' that sets the camera projection to
 * perspective when the main element is not a 2D image or an orthomosaic.
 *
 * @param main The current main element in the flywalk scene
 * @param flyWalkAnimation The current flywalk animation
 * @param previousMainNull Whether the previous main element is null
 */
export function useOrthoCameraForImages(
  main: FlyWalkSceneActiveElement | null,
  flyWalkAnimation: FlyWalkAnimation | null,
  previousMainNull: boolean,
): void {
  const { cameraProjection, setCameraProjection, onPerspectiveToOrtho } =
    useActiveCamera();

  // The only temporal fact we can't derive from current props: whether an animation
  // toward the current image was in progress, so we can detect the moment it ends.
  // A ref keeps this out of the render loop.
  const wasAnimatingToImage = useRef(false);

  useLayoutEffect(() => {
    // When the mode is being initialized and there is no main object, do nothing.
    // It could be that we need a perspective projection, but it could also be that
    // a deep link to a photo wants an ortho projection.
    if (main === null) return;

    const isImage = isIElementImg2d(main) || isIElementOrthomosaic(main);
    if (!isImage) {
      // Perspective is restored by usePerspectiveCameraForNonImages.
      wasAnimatingToImage.current = false;
      return;
    }

    if (flyWalkAnimation) {
      // Still animating toward the image; only switch once it finishes.
      wasAnimatingToImage.current = true;
      return;
    }

    // Image is now shown without animation: either the animation just finished,
    // or it was set directly via a deep link (previous main was null).
    const arrivedAtImage = wasAnimatingToImage.current || previousMainNull;
    wasAnimatingToImage.current = false;
    if (arrivedAtImage && cameraProjection !== CameraViewType.orthographic) {
      setCameraProjection(CameraViewType.orthographic);
    }
  }, [
    main,
    flyWalkAnimation,
    previousMainNull,
    cameraProjection,
    setCameraProjection,
  ]);

  const { getState } = useAppStore();

  const toOrtho = useCallback(
    (switchEvent: CameraSwitchEvent) => {
      if (!main) return;
      if (isIElementImg2d(main)) {
        // Setup the incoming ortho camera with the correct vertical size to match the photo height
        const pose = selectIElementWorldTransform(main.id)(getState());
        const maxPixelSize = Math.max(main.pixelWidth, main.pixelHeight);
        const halfHeight =
          (PHOTO_SIZE * pose.scale[0] * 0.5 * main.pixelHeight) / maxPixelSize;
        switchEvent.orthoCamera.top = halfHeight;
        switchEvent.orthoCamera.bottom = -halfHeight;
        switchEvent.orthoCamera.zoom = 1.0;
      } else if (isIElementOrthomosaic(main)) {
        // Setup the incoming ortho camera with the correct vertical size to match the orthomosaic image height
        const pose = selectIElementWorldTransform(main.id)(getState());
        const sheetSize = getSheetSize(main);
        const halfHeight = sheetSize.height * 0.5 * pose.scale[0];
        switchEvent.orthoCamera.top = halfHeight;
        switchEvent.orthoCamera.bottom = -halfHeight;
        switchEvent.orthoCamera.zoom = 1.0;
      }
    },
    [main, getState],
  );

  useTypedEvent(onPerspectiveToOrtho, toOrtho);

  // Ensure that, when this hook is unmounted, the camera projection is restored to perspective
  useNonExhaustiveEffect(
    () => () => {
      setCameraProjection(CameraViewType.perspective);
    },
    [],
  );
}

/**
 * Before the animation even starts, ensures that the camera projection is set to perspective
 * when the main element is not a 2D image or an orthomosaic.
 *
 * @param main The current main element in the flywalk scene
 */
export function usePerspectiveCameraForNonImages(
  main: FlyWalkSceneActiveElement | null,
): void {
  const { cameraProjection, setCameraProjection } = useActiveCamera();

  useLayoutEffect(() => {
    if (!main) return;
    const isImage = isIElementImg2d(main) || isIElementOrthomosaic(main);
    if (!isImage && cameraProjection !== CameraViewType.perspective) {
      setCameraProjection(CameraViewType.perspective);
    }
  }, [main, cameraProjection, setCameraProjection]);
}
